<template>
  <section class="invoice-preview-wrapper">
    <b-row v-if="invoiceData" class="invoice-preview">

      <!-- Col: Left (Invoice Container) -->
      <b-col cols="12" xl="9" md="8" id="divToPrint" ref="printMe">
        <b-card no-body class="invoice-preview-card pdf-item">
          <!-- Header -->
          <b-card-body class="invoice-padding pb-0">

            <div class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0">

              <div>
                <b-row class="align-items-start">
                  <b-col
                    v-if="setting.imageBase64"
                    cols="auto"
                    class="pr-3 mb-2 mb-md-0 flex-shrink-0"
                  >
                    <b-img
                      :src="setting.imageBase64"
                      fluid
                      alt="Logo société"
                      style="max-height: 120px; max-width: 160px; width: auto; object-fit: contain;"
                    />
                  </b-col>
                  <b-col class="pl-0">
                    <h3 class="text-primary">
                      {{ setting.societe }}
                    </h3>
                    <b-card-text class="mb-25">
                      {{ setting.adresse1 }}
                    </b-card-text>
                    <b-card-text class="mb-25" v-if="setting.adresse2">
                      {{ setting.adresse2 }}
                    </b-card-text>
                    <b-card-text class="mb-25" v-if="setting.adresse3">
                      {{ setting.adresse3 }}
                    </b-card-text>
                    <b-card-text class="mb-0">
                      {{ setting.codepostal }} {{ setting.ville }}
                    </b-card-text>
                    <b-card-text class="mb-0">
                      {{ setting.pays }}
                    </b-card-text>
                  </b-col>
                </b-row>
              </div>

              <!-- Header: Right Content -->
              <div class="invoice-number-date mt-md-0 mt-2">
                <div class="d-flex align-items-center mb-1">
                  <b-card-text><strong>Statut de commande : </strong> {{ this.invoiceData.statut }}</b-card-text>
                </div>
                <div class="d-flex align-items-center mb-1">
                  <b-card-text><strong>Date de commande : </strong> {{
      moment(this.invoiceData.datecommande).locale('fr').format('DD/MM/YYYY') }}</b-card-text>
                </div>
                <div class="d-flex align-items-center mb-1">
                  <b-card-text><strong>Date de livraison : </strong> {{
      moment(this.invoiceData.datelivraison).locale('fr').format('DD/MM/YYYY') }}</b-card-text>
                </div>
                <div class="d-flex align-items-center mb-1">
                  <b-card-text><strong>Mode de paiement : </strong> {{ this.invoiceData.client.modepaiement
                    }}</b-card-text>
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing">

          <!-- Invoice Client & Payment Details -->
          <b-card-body v-if="invoiceData.client" class="invoice-padding pt-0">
            <b-row class="invoice-spacing">

              <!-- Col: Invoice To -->
              <b-col cols="12" xl="6" class="mb-lg-1">
                <h6 class="mb-2">
                  Adresse de livraison :
                </h6>

                <!-- Selected Client -->
                <div v-if="this.invoiceData" class="mt-1">
                  <h6 class="mb-25">
                    {{ this.invoiceData.client.nom }}
                  </h6>
                  <b-card-text class="mb-25">{{ this.invoiceData.client.adresseLivraison.adresse1 }}</b-card-text>
                  <b-card-text v-if="this.invoiceData.client.adresseLivraison.adresse2" class="mb-25">{{
      this.invoiceData.client.adresseLivraison.adresse2 }}</b-card-text>
                  <b-card-text v-if="this.invoiceData.client.adresseLivraison.adresse3" class="mb-25">{{
      this.invoiceData.client.adresseLivraison.adresse3 }}</b-card-text>
                  <b-card-text class="mb-25">{{ invoiceData.client.adresseLivraison.codepostal }} {{
      invoiceData.client.adresseLivraison.ville }}</b-card-text>
                  <b-card-text class="mb-25">{{ this.invoiceData.client.adresseLivraison.pays }}</b-card-text>
                </div>
              </b-col>

              <!-- Col: Payment Details -->
              <b-col cols="12" xl="6" class="mb-lg-1">
                <h6 class="mb-2">
                  Adresse de facturation :
                </h6>

                <!-- Selected Client -->
                <div v-if="this.invoiceData" class="mt-1">
                  <h6 class="mb-25">
                    {{ this.invoiceData.client.nom }}
                  </h6>
                  <b-card-text class="mb-25">{{ this.invoiceData.client.adresseFacturation.adresse1 }}</b-card-text>
                  <b-card-text v-if="this.invoiceData.client.adresseFacturation.adresse2" class="mb-25">{{
      this.invoiceData.client.adresseFacturation.adresse2 }}</b-card-text>
                  <b-card-text v-if="this.invoiceData.client.adresseFacturation.adresse3" class="mb-25">{{
      this.invoiceData.client.adresseFacturation.adresse3 }}</b-card-text>
                  <b-card-text class="mb-25">{{ invoiceData.client.adresseFacturation.codepostal }} {{
      invoiceData.client.adresseFacturation.ville }}</b-card-text>
                  <b-card-text class="mb-25">{{ this.invoiceData.client.adresseFacturation.pays }}</b-card-text>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-table-lite responsive :items="invoiceData.products" :fields="[
            { key: 'codearticle', label: 'Article' },
            { key: 'prix', label: 'Prix unitaire' },
            { key: 'quantite', label: 'Quantité' },
            { key: 'totalHT', label: 'Total HT' }
          ]">
            <template #cell(prix)="data">
              <span v-if="!hasGammesPrix(data.item)">{{ formatMoney(data.value) }}</span>
              <span v-else style="font-size: 0.8em; display: block;">
                <div v-for="(g, idx) in groupedGammesTarifs(data.item)" :key="`${idx}-${g.prix}`">
                  {{ g.labels.join(' ') }} : {{ formatMoney(g.prix) }} {{ setting.devise }}
                </div>
              </span>
              <span v-if="data.item.variant" style="font-size: 0.8em; display: block;">Prix de l'assortiment : {{ data.item.prixpack }}</span>
            </template>
            
            <template #cell(quantite)="data">
              {{ data.value }}
              <span v-if="data.item.variant" style="font-size: 0.8em; display: block;">Quantité d'assortiment : {{ data.item.quantitepack }}</span>
            </template>
            
            <template #cell(totalHT)="data">
              {{ formatMoney(itemTotalHT(data.item)) }}
            </template>
            
            <template #cell(codearticle)="data">
              <div class="d-flex align-items-start invoice-product-cell">
                <b-img
                  thumbnail
                  :src="articleRowImage(data.item)"
                  alt=""
                  class="invoice-product-thumb flex-shrink-0 mr-1"
                  style="max-height: 72px; max-width: 72px; width: auto; object-fit: contain;"
                />
                <div class="flex-grow-1 min-w-0">
                  <b-card-text class="font-weight-bold mb-25" v-if="!data.item.variant">
                    {{ data.item.codearticle }}
                  </b-card-text>
                  <b-card-text class="font-weight-bold mb-25" v-else>
                    {{ data.item.variant }}
                  </b-card-text>
                  <b-card-text class="text-nowrap">
                    {{ data.item.nom }}
                  </b-card-text>

                  <b-card-text style="font-size: 80%">
                    <div v-for="(gamme, gammeindex) in data.item.gammes" :key="gammeindex" style="float: left;">
                      <div style="border: 1px solid #ddd; text-align: center; padding: 2px 5px;">{{ gamme.libelle }} </div>
                      <div style="border: 1px solid #ddd; text-align: center; padding: 2px 5px;"> {{ gamme.value }} </div>
                    </div>
                  </b-card-text>
                </div>
              </div>
            </template>
            
          </b-table-lite>

          <!-- Invoice Description: Total -->
          <b-card-body class="invoice-padding pb-0">
            <b-row>

              <!-- Col: Sales Persion -->
              <b-col cols="12" md="6" class="mt-md-0 mt-3" order="2" order-md="1">
                <b-card-text class="mb-0">
                  <h5>Commercial</h5>
                  <span style="font-size: 80%" class="font-weight-bold">Nom : </span>
                  <span style="font-size: 80%" class="ml-75">{{ invoiceData.nomcommercial }}</span><br>
                  <span style="font-size: 80%" class="font-weight-bold">Email : </span>
                  <span style="font-size: 80%" class="ml-75">{{ invoiceData.commercial }}</span><br>
                  <span style="font-size: 80%" class="font-weight-bold">Téléphone : </span>
                  <span style="font-size: 80%" class="ml-75">{{ invoiceData.telcommercial }}</span><br>
                </b-card-text>
              </b-col>

              <!-- Col: Total -->
              <b-col cols="12" md="6" class="mt-md-0 d-flex justify-content-end" order="1" order-md="2">
                <div class="invoice-total-wrapper">
                  <div class="invoice-total-item mb-2" style="font-size: 80%">
                    <p class="invoice-total-title">
                      Total paires :
                    </p>
                    <p class="invoice-total-amount">
                      {{ totalPaire }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      Sous-total HT :
                    </p>
                    <p class="invoice-total-amount">
                      {{ totalMontant }} {{ setting.devise }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      Remise HT :
                    </p>
                    <p class="invoice-total-amount">
                      {{ invoiceData.remisepanier }} %
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      Estimation frais de port HT :
                    </p>
                    <p class="invoice-total-amount">
                      {{ totalFraisPortHT }} {{ setting.devise }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      TVA :
                    </p>
                    <p class="invoice-total-amount">
                      {{ totalMontantTva }} {{ setting.devise }}
                    </p>
                  </div>
                  <hr class="my-50">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      Total TTC :
                    </p>
                    <p class="invoice-total-amount">
                      {{ totalMontantTTC }} {{ setting.devise }}
                    </p>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing">

          <!-- Note -->
          <b-card-body class="invoice-padding pt-0">
            <span class="font-weight-bold">Note: </span>
            <span>{{ invoiceData.note }}</span>
          </b-card-body>

          <b-card-body class="invoice-padding pt-0">
            <span class="font-weight-bold">Signature: </span>
            <img v-if="this.invoiceData.sign.isEmpty == false" :src=this.invoiceData.sign.data>
          </b-card-body>

          <!-- Mentions légales -->
          <b-card-body
            v-if="mentionsLegalesDisplay"
            class="invoice-padding pt-2 border-top invoice-mentions-legales"
          >
            <div class="text-muted small invoice-mentions-legales__text" style="width: 100%;">
              {{ mentionsLegalesDisplay }}
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col cols="12" md="4" xl="3" class="invoice-actions">
        <b-card>

          <!-- Button: Send Invoice -->
          <!-- <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" v-b-toggle.sidebar-send-invoice variant="primary"
            class="mb-75" block>
            Envoyer par mail
          </b-button> -->

          <!-- Button: Download -->
          <b-button v-if="appCapacitor == false" v-ripple.400="'rgba(186, 191, 199, 0.15)'" variant="outline-secondary"
            class="mb-75" block @click="downloadDocument">
            Télécharger
          </b-button>

          <!-- Button: Print -->
          <b-button v-if="appCapacitor == false" v-ripple.400="'rgba(186, 191, 199, 0.15)'" variant="outline-secondary"
            class="mb-75" block @click="printInvoice">
            Imprimer
          </b-button>

          <!-- Button: Download -->
          <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" variant="dark" class="mb-75" block
            @click="$router.push({ name: 'listcommande' })">
            Retour
          </b-button>

        </b-card>
      </b-col>
    </b-row>

    <b-sidebar id="sidebar-send-invoice" sidebar-class="sidebar-lg" bg-variant="white" shadow backdrop no-header right>
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            Envoi de commande
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />

        </div>

        <!-- Body -->
        <b-form class="p-2" ref="form" @submit.prevent="sendMail">

          <!-- From -->
          <b-form-group label="De" label-for="from">
            <b-form-input id="from" v-model="commercial.email" trim type="email" />
          </b-form-group>

          <!-- To -->
          <b-form-group label="Pour" label-for="to">
            <b-form-input id="to" v-model="invoiceData.client.email" trim type="email" />
          </b-form-group>

          <!-- Subject -->
          <b-form-group label="Sujet" label-for="subject">
            <b-form-input id="subject" v-model="sujet" trim />
          </b-form-group>

          <!-- Message -->
          <b-form-group label="Message" label-for="message">
            <quill-editor v-model="message" :options="snowOption" />
          </b-form-group>

          <!-- ? File/Attachement -->
          <b-badge variant="light-primary">
            <feather-icon icon="LinkIcon" />
            <span class="ml-50">Facture jointe</span>
          </b-badge>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="mr-2" type="submit"
              @click="hide">
              Envoyer
            </b-button>
            <b-button v-ripple.400="'rgba(186, 191, 199, 0.15)'" variant="outline-secondary" @click="hide">
              Annuler
            </b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
    <!-- <invoice-sidebar-send-invoice /> -->
  </section>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BForm,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BTable,
  BLink,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormGroup,
  BFormCheckbox,
  BFormTextarea,
  BFormCheckboxGroup,
  BFormSpinbutton,
  BFormRadioGroup,
  BImg,
  BCardText,
  BFormRadio,
  BFormDatepicker,
  BPopover,
  BTableLite,
  BSidebar,
  BBadge,
  VBToggle,
} from 'bootstrap-vue'

import { CapacitorHttp } from '@capacitor/core';
import Vue from 'vue'
import Logo from '@core/layouts/components/Logo.vue'
import Ripple from 'vue-ripple-directive'
import { quillEditor } from 'vue-quill-editor'
import PouchDB from 'pouchdb'
import moment from 'moment'
import axios from 'axios'

import jsPDF from 'jspdf'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BTable,
    BLink,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormGroup,
    BFormCheckbox,
    BFormTextarea,
    BFormCheckboxGroup,
    BFormSpinbutton,
    BFormRadioGroup,
    BImg,
    BCardText,
    BFormRadio,
    BFormDatepicker,
    BPopover,
    BTableLite,
    BSidebar,
    BBadge,
    VBToggle,
    quillEditor,

    Logo,

  },
  data() {
    return {
      commercial: JSON.parse(localStorage.commercial),
      moment: moment,
      setting: JSON.parse(localStorage.setting),
      localDB: new PouchDB(localStorage.getItem('database')),
      remoteDB: new PouchDB(localStorage.getItem('url') + '/' + localStorage.getItem('database')),
      sujet: '',
      to: '',
      attachementDoc: '',
      message: '',
      snowOption: {
        theme: 'snow',
        placeholder: 'Ajouter un commentaire',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
          ],
        },
      },
      invoiceData: {
        client: {
          adresseLivraison: '',
          adresseFacturation: ''
        },
        products: [],
        totalmontant: 0,
        totalmontanttva: 0,
        totalmontantttc: 0,
        remisepanier: 0,
        consent: '',
        note: '',
        commercial: '',
        output: ''
      },
      appCapacitor: false,
      os: '',
    }
  },
  computed: {
    mailtoVar() {
      let myvar
      let messagetext
      messagetext = this.message
      messagetext = messagetext.replace('  ', ' ')
      messagetext = messagetext.replace(/<\/p>/ig, '\n')
      messagetext = messagetext.replace(/<[^>]+>/ig, '')
      myvar = 'mailto:' + this.to + '?subject=' + this.sujet + '&body=' + encodeURIComponent(messagetext) + '&attachment="/Users/arnaud/Desktop/req.txt"'
      return myvar
    },
    mentionsLegalesDisplay() {
      const m = this.setting && this.setting.mentions_legales
      if (m == null) return ''
      const t = String(m).trim()
      return t || ''
    },
    // Total paires
    totalPaire() {
      this.invoiceData.totalPaire = 0
      if (this.invoiceData.products) {
        this.invoiceData.products.forEach(i => { this.invoiceData.totalPaire += parseInt(i.quantite) })
      }
      return this.invoiceData.totalPaire
    },
    // Total montant HT
    totalMontant() {
      this.invoiceData.totalmontant = 0
      if (this.invoiceData.products) {
        this.invoiceData.products.forEach(i => { this.invoiceData.totalmontant += this.itemTotalHT(i) })
      }
      return this.formatMoney(this.invoiceData.totalmontant)
    },
    // Total frais de port HT
    totalFraisPortHT() {
      this.totalMontant
      this.totalPaire
      this.invoiceData.totalfraisport = this.computeFraisPortHT()
      return this.formatMoney(this.invoiceData.totalfraisport)
    },
    // Total TVA
    totalMontantTva() {
      this.invoiceData.totalmontanttva = 0
      if (this.invoiceData.products) {
        if (this.invoiceData.client.eligibleTVA == 1) {
          this.invoiceData.products.forEach(i => {
            this.invoiceData.totalmontanttva += (this.itemTotalHT(i) * (this.toNumber(i.tva) / 100))
          })
          if (!this.isFrancoPort()) {
            this.invoiceData.totalmontanttva += this.toNumber(this.computeFraisPortHT()) * 0.2
          }
        }
      }
      return this.formatMoney(this.invoiceData.totalmontanttva)
    },
    // Total montant TTC
    totalMontantTTC() {
      this.invoiceData.totalmontantttc = 0
      if (this.invoiceData.products) {
        const remise = this.toNumber(this.invoiceData.remisepanier) / 100
        this.invoiceData.products.forEach(i => {
          const ht = this.itemTotalHT(i)
          if (this.invoiceData.client.eligibleTVA == 1) {
            this.invoiceData.totalmontantttc += (ht * (1 - remise) * (1 + (this.toNumber(i.tva) / 100)))
          }
          else {
            this.invoiceData.totalmontantttc += (ht * (1 - remise))
          }
        })
        if (!this.isFrancoPort()) {
          const fraisPort = this.toNumber(this.computeFraisPortHT())
          if (this.invoiceData.client.eligibleTVA == 1) {
            this.invoiceData.totalmontantttc += fraisPort * 1.2
          }
          else {
            this.invoiceData.totalmontantttc += fraisPort
          }
        }
      }
      return this.formatMoney(this.invoiceData.totalmontantttc)
    },
  },
  mounted() {
    this.os = this.detectOS();
  },
  created() {
    this.initializeCommande()
    if (window.Capacitor.isNative == true) { this.appCapacitor = true }
    else { this.appCapacitor = false }
    console.log('capacitor', window.Capacitor.isNative);
  },
  methods: {
    toNumber(value) {
      const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : Number(value)
      return Number.isFinite(n) ? n : 0
    },
    hasSettingValue(value) {
      return value !== '' && value !== null && value !== undefined
    },
    isFrancoPort() {
      if (this.commercial?.showfraisport === 'false') return true
      if (this.hasSettingValue(this.setting.franco) && this.totalPaire > this.toNumber(this.setting.franco)) return true
      if (this.hasSettingValue(this.setting.francomontant) && this.toNumber(this.invoiceData.totalmontant) > this.toNumber(this.setting.francomontant)) return true
      return false
    },
    computeFraisPortHT() {
      if (this.isFrancoPort()) return 0
      if (this.hasSettingValue(this.setting.fraisportmontant)) {
        return this.toNumber(this.setting.fraisportmontant)
      }
      if (this.hasSettingValue(this.setting.fraisport) && this.invoiceData.products) {
        return this.invoiceData.products.reduce(
          (sum, i) => sum + (this.toNumber(i.quantite) * this.toNumber(this.setting.fraisport)),
          0,
        )
      }
      return 0
    },
    formatMoney(value) {
      return this.toNumber(value).toFixed(2)
    },
    itemTotalHTBase(item) {
      if (!item) return 0

      const prixProduit = this.toNumber(item.prix)
      const quantite = this.toNumber(item.quantite)

      if (Array.isArray(item.gammes) && item.gammes.length > 0) {
        return item.gammes.reduce((sum, gamme) => {
          const q = this.toNumber(gamme?.value)
          if (!q) return sum

          const prixGamme = Object.prototype.hasOwnProperty.call(gamme, 'prix') ? this.toNumber(gamme?.prix) : null
          const unit = prixGamme !== null ? prixGamme : prixProduit
          return sum + (unit * q)
        }, 0)
      }

      return prixProduit * quantite
    },
    itemTotalHT(item) {
      const base = this.itemTotalHTBase(item)
      const remise = this.toNumber(item?.remise)
      return base * (1 - (remise / 100))
    },
    itemTotalTTC(item) {
      const ht = this.itemTotalHT(item)
      const tva = this.toNumber(item?.tva)
      return ht * (1 + (tva / 100))
    },
    hasGammesPrix(item) {
      return Array.isArray(item?.gammes) && item.gammes.some(g => Object.prototype.hasOwnProperty.call(g, 'prix'))
    },
    groupedGammesTarifs(item) {
      if (!Array.isArray(item?.gammes)) return []

      const groups = new Map()
      item.gammes.forEach(g => {
        const unit = Object.prototype.hasOwnProperty.call(g, 'prix') ? this.toNumber(g?.prix) : this.toNumber(item?.prix)
        const key = unit.toFixed(6)
        const labels = groups.get(key) || { prix: unit, labels: [] }
        labels.labels.push(String(g?.libelle ?? '').trim())
        groups.set(key, labels)
      })

      return Array.from(groups.values()).map(g => ({
        prix: g.prix,
        labels: g.labels.filter(Boolean),
      }))
    },
    detectOS() {
      const platform = navigator.platform;
      if (platform.indexOf('Win') !== -1) return 'Windows';
      if (platform.indexOf('Mac') !== -1) return 'Mac OS';
      if (platform.indexOf('Linux') !== -1) return 'Linux';
      return 'Unknown';
    },
    articleRowImage(item) {
      if (item && item.image) return item.image
      return require('@/assets/images/emptyimg.png')
    },
    //Initialize Commande
    async initializeCommande() {
      // await this.localDB.get('642961ae-4134-472a-a9d4-a3d9b7b3ef2c', this.setcommandes)
      await this.localDB.get(this.$route.params.id, this.setcommandes)
    },
    //Affichage de la commande
    async setcommandes(err, doc) {
      if (err) {
        console.log('err', err)
      }

      this.invoiceData.client.email = ''
      this.invoiceData = doc
      console.log('invoice', doc)

      await this.localDB.find({ selector: { type: 'articles' } },).then(result => {
        console.log('result', result.docs);
        // this.itemsOptionsFind = result.docs
        for (let i = 0; i < doc.products.length; i++) {

          var variants = result.docs.filter(item => { return item.codearticle === doc.products[i].codearticle })
          console.log('variants', variants);
          this.invoiceData.products[i].variants = variants[0].variants

          if (!variants[0]._attachments) {
            console.log('no attachement')
            this.invoiceData.products[i].image = require('@/assets/images/emptyimg.png')
          } else {
            // console.log('attached', Object.keys(doc.docs[i]._attachments)[0])
            this.localDB.getAttachment(variants[0]._id, Object.keys(variants[0]._attachments)[0]).then(
              (blobOrBuffer) => {
                // console.log('url', URL.createObjectURL(blobOrBuffer))
                this.invoiceData.products[i].image = URL.createObjectURL(blobOrBuffer)
              },
              (error) => {
                // console.log(error)
                this.invoiceData.products[i].image = require('@/assets/images/emptyimg.png')
              }
            );
          }

        }
      })

      this.sujet = 'Votre commande ' + this.setting.societe
      this.message = '<p>Bonjour,</p><p>Vous trouverez en pièce jointe votre commande du ' + moment(this.invoiceData.datecommande).locale('fr').format('DD/MM/YYYY') + ' </p><p>Cordialement</p>'

      await this.attachementDocument()
    },
    // Impression de la commande
    printInvoice() {
      window.print()
    },
    // Téléchargement de la commande
    downloadDocument() {
      var doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: false,
        floatPrecision: 16 // or "smart", default is 16
      });
      doc.html(document.getElementById('divToPrint'), {
        callback: doc => {
          doc.save('commande.pdf');
          // doc.output('datauri');
          // doc.output('dataurlnewwindow');
        },
        x: 10,
        y: 10,
        width: 250,
        windowWidth: 1370,
      });
    },
    // Création fichier pièce jointe
    async attachementDocument() {
      // jsPDF
      var doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: false,
        floatPrecision: 16 // or "smart", default is 16
      });
      await doc.html(document.getElementById('divToPrint'), {
        x: 10,
        y: 10,
        width: 250,
        windowWidth: 1370,
      });
      this.attachementDoc = doc.output('datauristring')
      // this.attachementDoc = this.attachementDoc.replace('data:application/pdf;filename=generated.pdf;base64,', '' )
      // console.log('attachementDoc', this.attachementDoc)

    },
    // Envoi Email
    sendMail() {
      let sllType

      if (this.commercial.smtpsll == "false") { sllType = "tls" }
      else { sllType = "ssl" }

      let data = {
        Messages:
        {
          fromEmail: this.commercial.email,
          toEmail: this.invoiceData.client.email,
          Subject: this.sujet,
          TextPart: "Bonjour, vous trouverez en pièce jointe votre commande du " + moment(this.invoiceData.datecommande).locale('fr').format('DD/MM/YYYY') + ". Cordialement",
          HTMLPart: "<p>Bonjour,</p><p>Vous trouverez en pièce jointe votre commande du " + moment(this.invoiceData.datecommande).locale('fr').format('DD/MM/YYYY') + "</p><p>Cordialement</p>",
          Attachement:
          {
            "FileName": "commande.pdf",
            "Encoding": "base64",
            "Type": "application/pdf",
            "Base64Content": this.attachementDoc
          },
          Setting:
          {
            "smtpServer": this.commercial.srvsmtp,
            "smtpPort": this.commercial.portsmtp,
            "smtpSLL": sllType,
            "smtpLogin": this.commercial.loginsmtp,
            "smtpPassword": this.commercial.passwordsmtp
          }
        }

      }

      let url = ''
      if (Vue.config.devtools == true) {
        url = '/sendmail'
      }
      else {
        url = process.env.VUE_APP_API_URL
      }

      axios.defaults.withCredentials = true
      // axios.post('/sendmail',
      // axios.post(process.env.VUE_APP_API_URL,
      axios.post(url,
        data,
        {
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Authorization': 'Basic TjM4a0xXcGFZaGtoOlE5UFJNNmFpTjJ5YQ==',
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      ).then(response => {
        console.log('Axios response', response.data)
        if (response.data.message != 'Message has been sent') {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Problème de configuration mail',
              icon: 'CoffeeIcon',
              variant: 'warning',
              text: 'Vérifier votre configuration d\'envoi de mail : ' + response.data.message,
            },
          })
        }
        else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Mail envoyé',
              icon: 'CoffeeIcon',
              variant: 'success',
              text: 'Votre commande a été envoyé par mail avec succès',
            },
          })
        }
      }).catch(error => {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Problème réseau',
            icon: 'CoffeeIcon',
            variant: 'warning',
            text: error,
          },
        })
        console.log('Axios error', error)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
</style>

<style lang="scss">
@import '@core/scss/vue/libs/quill.scss';

.comment_description img {
  max-width: 400px;
  height: auto;
}

.ql-container,
.ql-editor {
  min-height: inherit;
}

.invoice-content img {
  max-width: 100% !important;
}

.invoice-preview .invoice-product-thumb {
  width: 72px;
  height: 72px;
  object-fit: contain;
}

.invoice-preview .invoice-total-wrapper {
  max-width: 20rem !important;
}

.invoice-preview .invoice-total-wrapper .invoice-total-item .invoice-total-title {
  width: 300px;
  text-align: left;
}

.invoice-preview .invoice-total-wrapper .invoice-total-item .invoice-total-amount {
  width: 200px;
  text-align: right;
}

.invoice-preview .invoice-mentions-legales__text {
  white-space: pre-wrap;
  font-size: 0.6rem;
}

@media print {

  // Global Styles
  body {
    background-color: transparent !important;
  }

  nav.header-navbar {
    display: none;
  }

  .main-menu {
    display: none;
  }

  .header-navbar-shadow {
    display: none !important;
  }

  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }

  footer.footer {
    display: none;
  }

  .card {
    background-color: transparent;
    box-shadow: none;
  }

  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
            >.col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
}
</style>
